import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../../lib/api";

const monthLabel = (m) => {
  const [y, mo] = (m || "").split("-").map(Number);
  return y ? new Date(y, mo - 1, 1).toLocaleDateString("en-AU", { month: "short" }) : m;
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-ink/10 bg-white p-5" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink/55">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold text-ink">{value}</p>
  </div>
);

export const Insights = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/insights").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;
  if (!data) return <p className="text-sm text-ink/60" data-testid="insights-error">Insights couldn't load right now.</p>;

  const months = (Array.isArray(data.months) ? data.months : []).map((m) => ({ ...m, label: monthLabel(m.month) }));
  const services = Array.isArray(data.services) ? data.services : [];
  const top = Math.max(1, ...services.map((s) => s.count));

  return (
    <div className="grid gap-5" data-testid="insights">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="This month" value={data.this_month ?? 0} testId="insight-this-month" />
        <Stat label="Last month" value={data.last_month ?? 0} testId="insight-last-month" />
        <Stat label="Awaiting reply" value={data.status?.new ?? 0} testId="insight-new" />
        <Stat label="All time" value={data.total ?? 0} testId="insight-total" />
      </div>
      <div className="grid gap-5 lg:grid-cols-5">
        <div className="rounded-2xl border border-ink/10 bg-white p-5 lg:col-span-3" data-testid="insight-months-chart">
          <h3 className="font-display text-lg font-semibold text-ink">Enquiries per month</h3>
          <div className="mt-4 h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#4B5563" }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#4B5563" }} />
                <Tooltip cursor={{ fill: "rgba(29,53,87,0.06)" }} formatter={(v) => [v, "Enquiries"]} labelFormatter={(l) => l} />
                <Bar dataKey="count" fill="#E63946" radius={[6, 6, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-ink/10 bg-white p-5 lg:col-span-2" data-testid="insight-services">
          <h3 className="font-display text-lg font-semibold text-ink">Most requested services</h3>
          <ul className="mt-4 grid gap-3">
            {services.map((s) => (
              <li key={s.service}>
                <div className="flex justify-between text-sm"><span className="text-ink">{s.service}</span><span className="font-semibold text-ink">{s.count}</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-navy" style={{ width: `${(s.count / top) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
